import { View, FlatList, StyleSheet, Platform, TouchableOpacity } from "react-native";
import { useRef, useEffect, useMemo } from "react";
import { Text, TouchableRipple } from "react-native-paper";
import { gql, useQuery } from "@apollo/client";

import { ScreenProps } from "./types";
import { flatList, ScrollView } from "../components/styled";
import { ACTIVITY_FRAGMENT, Activity } from "./fragments";
import { ScreenContainer } from "../components";
import ActivityImage from "../components/ActivityImage";
import { groupBy, map, partition } from "lodash";
import moment from "moment";
import { ActivityUpdate, getActivePreviousPlan, getActivityUpdates } from "./utils";

const DISABLE_FILTER = "All";

export interface Plan {
  startsOn: string;
  current?: boolean;
  submissionWindowClosesOn?: string | null;
  activities: Activity[];
}

interface Query {
  currentPlan: Plan;
  planTransition?: { previousPlan: Plan };
}

export const QUERY = gql`
  query activitiesQuery {
    currentPlan: currentDeferredCompPlan {
      id
      startsOn
      activities {
        ...ActivityFragment
      }
    }
    planTransition: deferredCompPlanTransition {
      previousPlan {
        id
        startsOn
        submissionWindowClosesOn
        activities {
          ...ActivityFragment
        }
      }
    }
  }
  ${ACTIVITY_FRAGMENT}
`;

const TagList = flatList<string>();

const styles = StyleSheet.create({
  // @ts-ignore
  card: {
    ...Platform.select({ web: { cursor: "pointer" } }),
  },
});

interface ActivityData extends Activity {
  plan: Plan;
}

const ActivityCard = ({
  activities,
  plans,
  code,
  onPress,
}: {
  activities: ActivityData[];
  plans: Plan[];
  code: string;
  onPress: () => void;
}) => {
  const { name, maxPoints, tags, imageUrl, badge } = useMemo(() => {
    const [[currentActivity], [oldActivity]] = partition(activities, (a) => a.plan.current);

    let badge: string | undefined = undefined;
    if (plans.length === 2) {
      const updates = getActivityUpdates(currentActivity, oldActivity);
      if (updates.includes(ActivityUpdate.New)) {
        badge = `New for ${moment(currentActivity.plan.startsOn).year()}`;
      } else if (updates.includes(ActivityUpdate.Unavailable)) {
        const currentPlan = plans.find((p) => p.current);
        badge = `Unavailable in ${moment(currentPlan?.startsOn).year()}`;
      } else if (updates.length > 0) {
        badge = "Updated";
      }
    }

    return { ...(currentActivity || oldActivity), badge };
  }, [code]);

  const className = "mb-6 sm:mx-3 bg-white border rounded-lg border-sky sm:w-72 md:w-80 active:shadow-md";
  return (
    <TouchableOpacity activeOpacity={1} className={className} style={styles.card} onPress={onPress}>
      <View className="flex items-center">
        <ActivityImage imageUrl={imageUrl} imageClassName="w-full rounded-t-lg h-40 bg-sky-light" />
      </View>

      <View className="m-4">
        <View className="flex-row flex-wrap">
          {tags.map(({ name }) => (
            <Text className="mr-2 text-small-normal text-primary font-400" key={name}>
              {name}
            </Text>
          ))}
        </View>

        <Text className="mt-0.5 font-700 text-large-tight">{name}</Text>

        <View className="flex flex-row items-center mt-3">
          <Text className="font-700 text-regular">{maxPoints}</Text>

          <Text className="ml-1 font-400 text-small">points max</Text>
        </View>
      </View>
      {!!badge && (
        <View
          testID="badge"
          className="bg-primary-light rounded-full px-2 py-1 absolute left-2 -top-3 overflow-hidden items-center justify-center"
        >
          <Text className="text-sky-white text-tiny-tight font-500">{badge}</Text>
        </View>
      )}
    </TouchableOpacity>
  );
};

const TagButton = ({ tag, onPress, current }: { tag: string; onPress: () => void; current: boolean }) => (
  <TouchableRipple onPress={onPress} disabled={current} testID="filter-button" className="rounded-lg">
    <View className={`rounded-lg ${current ? "bg-primary" : "bg-sky-light"}`}>
      <Text className={`my-0 pt-1 pb-2 px-6 text-regular-normal font-400 ${current ? "text-white" : "text-black"}`}>{tag}</Text>
    </View>
  </TouchableRipple>
);
const tagNames = (activities: Activity[]) =>
  [
    ...activities
      .reduce((acc, { tags }) => tags.reduce((acc, { name }) => acc.set(name, (acc.get(name) || 0) + 1), acc), new Map())
      .entries(),
  ]
    .sort(([_tagA, countA], [_tagB, countB]) => countB - countA)
    .map(([tag]) => tag);

const ActivitiesScreen = ({ navigation, route }: ScreenProps<"Activities">) => {
  const { data } = useQuery<Query>(QUERY);
  const filter = route.params?.tag || DISABLE_FILTER;
  const previousPlan = getActivePreviousPlan(data?.planTransition);
  const plans = [data?.currentPlan ? { ...data.currentPlan, current: true } : null, previousPlan].filter((p) => !!p) as Plan[];
  const activities = plans.flatMap((plan) => plan.activities.map((a) => ({ ...a, plan })));
  const tagList = useRef<FlatList<string>>(null);
  const tags = [DISABLE_FILTER].concat(tagNames(activities));
  const filteredActivities = activities.filter(
    ({ tags }) => filter === DISABLE_FILTER || tags.map(({ name }) => name).includes(filter)
  );

  const onCardPress =
    ({ code }: Activity) =>
    () => {
      navigation.navigate("Activity", { code });
    };

  useEffect(() => {
    tagList.current && tagList.current.scrollToItem({ item: filter, viewPosition: 0.5 });
  }, [tags, filter]);

  return (
    <ScreenContainer navigationTitle="Browse Activities" noBorder goBack={() => navigation.navigate("DeferredComp")}>
      <View className="py-6">
        <TagList
          ref={tagList}
          contentContainerStyle="px-6"
          onScrollToIndexFailed={() => {}}
          ItemSeparatorComponent={() => <View className="w-2" />}
          testID="filter-buttons"
          data={tags}
          horizontal
          showsHorizontalScrollIndicator={false}
          renderItem={({ item: tag }) => (
            <TagButton
              current={filter === tag}
              tag={tag}
              onPress={() => {
                navigation.setParams({ tag });
              }}
            />
          )}
          className="mb-2"
        />

        <ScrollView testID="activities" contentContainerStyle="px-6 mt-4 mb-6 pb-12 sm:flex-row sm:flex-wrap sm:justify-center">
          {map(
            groupBy(filteredActivities, (activity) => activity.code),
            (activities, code) => (
              <ActivityCard activities={activities} plans={plans} onPress={onCardPress(activities[0])} code={code} key={code} />
            )
          )}
        </ScrollView>
      </View>
    </ScreenContainer>
  );
};

export default ActivitiesScreen;
